"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { subscriptionSyncPlanIds } from "@/lib/study-plan-sync";
import { toast } from "sonner";
import { STUDY_SCHEDULE_CHANGED } from "@/lib/study-task-link";
import {
  formatMonthDay,
  STUDY_PLAN_LABELS,
  type StudyPlanId,
} from "@/lib/study-plan";
import type { PlanDynamicStateMap } from "@/lib/study-plan-state";
import { recoverStudyPlanRequest } from "@/lib/study-plan-request-error";
import {
  getAllPlansDynamicState,
  subscribeStudyPlan,
  type StudyPlanDynamicSnapshot,
} from "@/lib/server-actions/studyPlanActions";

export type { PlanDynamicStateMap };

export type SetPlanDynamicState = Dispatch<SetStateAction<PlanDynamicStateMap>>;
/** 낙관적 변경부터 서버 요청·보정·롤백까지 전체 쓰기 흐름을 감싼다. */
export type RunPendingWrite = <T>(work: () => Promise<T>) => Promise<T>;

type DynamicStateRead = () => Promise<void>;

/**
 * 학습 계획 동적 상태(getAllPlansDynamicState) 1회 fetch 보일러플레이트 공용 훅. (JCG-2486)
 *
 * state + loading + alive-guard 패턴이 useScheduleData·DashboardTodaySchedule·
 * StudyPlanOnTrackCard 세 곳에 복붙되어 있던 것을 통합한다.
 *
 * - `enabled`=false(게이트 미통과)면 fetch 를 생략한다(이때 loading 은 true 로 유지되며,
 *   호출부가 게이트 분기에서 먼저 반환하므로 값이 쓰이지 않는다).
 * - effect 본문에서 동기 setState 를 하지 않고 비동기 콜백에서만 set 하여
 *   cascading render 린트 규칙(react-hooks/set-state-in-effect)을 준수한다.
 * - `runPendingWrite` 중에는 회차 전환을 막고 초기·자동 조회를 미루며,
 *   마지막 쓰기가 끝나면 무효화하거나 미룬 조회를 한 번 보충한다.
 *
 * @param errorLabel fetch 실패 시 console.error 에 붙일 라벨(호출부별 식별).
 */
export function usePlanDynamicState(enabled: boolean, errorLabel: string) {
  const [snapshot, setSnapshot] = useState<StudyPlanDynamicSnapshot | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [writePending, setWritePending] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const selectingSessionRef = useRef(false);
  const pendingWriteCountRef = useRef(0);
  const inFlightReadRef = useRef<{
    requestId: number;
    retry: DynamicStateRead;
  } | null>(null);
  const deferredReadRef = useRef<DynamicStateRead | null>(null);

  const invalidateRead = useCallback(() => {
    const read = inFlightReadRef.current;
    if (!read) return;
    // 낙관적 변경보다 먼저 시작한 초기·자동 조회는 성공·실패 모두 반영하지 않는다.
    requestIdRef.current += 1;
    inFlightReadRef.current = null;
    deferredReadRef.current = read.retry;
  }, []);

  const setDynState = useCallback<SetPlanDynamicState>(
    (next) => {
      invalidateRead();
      setSnapshot((prev) => {
        if (!prev) return prev;
        const plans = typeof next === "function" ? next(prev.plans) : next;
        return plans ? { ...prev, plans } : prev;
      });
    },
    [invalidateRead],
  );

  const runPendingWrite = useCallback<RunPendingWrite>(
    async (work) => {
      // ref로 즉시 잠가 같은 tick의 회차 전환과 자동 조회도 차단한다.
      pendingWriteCountRef.current += 1;
      if (pendingWriteCountRef.current === 1) setWritePending(true);
      invalidateRead();
      try {
        return await work();
      } finally {
        pendingWriteCountRef.current -= 1;
        if (pendingWriteCountRef.current === 0) {
          setWritePending(false);
          // 모든 쓰기가 끝난 뒤, 무효화하거나 미뤄 둔 조회만 한 번 보충한다.
          const deferredRead = deferredReadRef.current;
          deferredReadRef.current = null;
          if (deferredRead) void deferredRead();
        }
      }
    },
    [invalidateRead],
  );

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = async () => {
      if (!alive) return;
      if (pendingWriteCountRef.current > 0) {
        // 재활성화 시에도 저장이 끝난 뒤 읽는다. snapshot이 없어도 이 함수로 재개한다.
        deferredReadRef.current = load;
        return;
      }
      deferredReadRef.current = null;
      const requestId = ++requestIdRef.current;
      inFlightReadRef.current = { requestId, retry: load };
      try {
        const res = await getAllPlansDynamicState();
        if (alive && requestId === requestIdRef.current) {
          setSnapshot(res);
          setLoadError(null);
        }
      } catch (e) {
        console.error(errorLabel, e);
        if (alive && requestId === requestIdRef.current) {
          if (recoverStudyPlanRequest(e, setLoadError)) return;
          setLoadError(
            "학습 일정을 불러오지 못했어요. 페이지를 새로고침해주세요.",
          );
        }
      } finally {
        if (inFlightReadRef.current?.requestId === requestId)
          inFlightReadRef.current = null;
        if (alive && requestId === requestIdRef.current) {
          setLoading(false);
          setSessionLoading(false);
        }
      }
    };
    void load();
    return () => {
      alive = false;
      if (inFlightReadRef.current?.retry === load)
        inFlightReadRef.current = null;
      if (deferredReadRef.current === load) deferredReadRef.current = null;
      requestIdRef.current += 1;
      selectingSessionRef.current = false;
    };
  }, [enabled, errorLabel]);

  const selectedSessionId = snapshot?.selectedExamSessionId;
  useEffect(() => {
    if (!enabled || !selectedSessionId) return;
    let alive = true;
    const refresh = async () => {
      if (!alive || selectingSessionRef.current) return;
      if (pendingWriteCountRef.current > 0) {
        deferredReadRef.current = refresh;
        return;
      }
      deferredReadRef.current = null;
      const requestId = ++requestIdRef.current;
      inFlightReadRef.current = { requestId, retry: refresh };
      try {
        const next = await getAllPlansDynamicState(selectedSessionId);
        if (alive && requestId === requestIdRef.current) {
          setSnapshot(next);
          setLoadError(null);
        }
      } catch (error) {
        if (
          alive &&
          requestId === requestIdRef.current &&
          !recoverStudyPlanRequest(error, setLoadError)
        )
          setLoadError(
            "최신 학습 일정을 확인하지 못했어요. 페이지를 새로고침해주세요.",
          );
      } finally {
        if (inFlightReadRef.current?.requestId === requestId)
          inFlightReadRef.current = null;
      }
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === STUDY_SCHEDULE_CHANGED) void refresh();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener(STUDY_SCHEDULE_CHANGED, refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      alive = false;
      if (inFlightReadRef.current?.retry === refresh)
        inFlightReadRef.current = null;
      if (deferredReadRef.current === refresh) deferredReadRef.current = null;
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener(STUDY_SCHEDULE_CHANGED, refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, [enabled, selectedSessionId]);

  const selectExamSession = useCallback(
    async (examSessionId: string) => {
      if (
        !enabled ||
        pendingWriteCountRef.current > 0 ||
        snapshot?.selectedExamSessionId === examSessionId
      )
        return;
      const requestId = ++requestIdRef.current;
      inFlightReadRef.current = null;
      deferredReadRef.current = null;
      selectingSessionRef.current = true;
      setSessionLoading(true);
      try {
        const next = await getAllPlansDynamicState(examSessionId);
        if (requestId === requestIdRef.current) {
          setSnapshot(next);
          setLoadError(null);
        }
      } catch (error) {
        console.error(errorLabel, error);
        if (requestId !== requestIdRef.current) return;
        if (recoverStudyPlanRequest(error, setLoadError)) return;
        toast.error("학습 일정 회차를 불러오지 못했어요.");
      } finally {
        if (requestId === requestIdRef.current) {
          selectingSessionRef.current = false;
          setSessionLoading(false);
        }
      }
    },
    [enabled, errorLabel, snapshot?.selectedExamSessionId],
  );

  return {
    dynState: snapshot?.plans ?? null,
    setDynState,
    loading,
    sessionLoading,
    writePending,
    runPendingWrite,
    loadError,
    setLoadError,
    selectExamSession,
    activeExamSessionId: snapshot?.activeExamSessionId ?? null,
    selectedExamSessionId: snapshot?.selectedExamSessionId ?? null,
    readOnly: snapshot?.readOnly ?? false,
  };
}

/**
 * 학습 계획 구독 플로우 공용 헬퍼: subscribe → refetch → 상태/오류 → toast. (JCG-2486)
 *
 * useScheduleData 와 DashboardTodaySchedule 에 동일하게 복붙되어 있던 흐름을 통합한다.
 * 재조회 실패 시 사본을 합성하지 않고 지속적인 조회 오류를 전달해 시작 CTA를 숨긴다.
 * 성공/실패 토스트까지 처리한 뒤 성공 여부를 반환한다. 호출부는 자체 pending/busy 상태만 관리한다.
 */
/**
 * 구독 직후 오늘과 구독 시작일의 관계(JCG-3652). 구독 시작일은 계획 페이지의 정규 시작일로 고정되므로
 * 오늘이 그날이 아니면 캘린더가 비어 있거나(early) 앞부분이 밀려 있다(late) — 각 화면이 이 값으로
 * "오늘 기준 다시 분배" 안내를 한 번만 보여준다.
 */
export type StartTiming = "onTime" | "early" | "late";
export type SubscribeResult = {
  planId: StudyPlanId;
  startDateISO: string;
  timing: StartTiming;
  syncPlanIds?: StudyPlanId[];
};

export function startTimingOf(
  startDateISO: string,
  todayISO: string,
): StartTiming {
  if (todayISO === startDateISO) return "onTime";
  return todayISO < startDateISO ? "early" : "late";
}

/**
 * 구독 성공 토스트 `[message, options]` (`toast.success(...studyStartedToast(r, hint))`).
 * hint=true 는 다시 분배 다이얼로그가 없는 화면(대시보드)용 — 학습 일정 화면의 버튼을 안내한다.
 * 일정 화면은 다이얼로그가 안내하므로 hint 없이 짧게 띄운다.
 */
export function studyStartedToast(
  result: SubscribeResult,
  hint: boolean,
): [string, { duration: number } | undefined] {
  const started = `${STUDY_PLAN_LABELS[result.planId]} 학습을 시작했어요!`;
  if (!hint || result.timing === "onTime") return [started, undefined];
  const startLabel = formatMonthDay(result.startDateISO);
  const guide =
    result.timing === "early"
      ? `계획은 ${startLabel}부터라 그날까지는 비어 있어요. 오늘부터 하려면 학습 일정에서 '오늘 기준 다시 분배'를 눌러주세요.`
      : `계획 시작일(${startLabel})이 지나 앞부분이 밀려 있어요. 학습 일정에서 '오늘 기준 다시 분배'로 맞출 수 있어요.`;
  return [`${started} ${guide}`, { duration: 7000 }];
}

/**
 * 구독 → 동적 상태 재조회. 오류 토스트는 여기서, 성공 토스트는 호출한 화면이 결과로 띄운다
 * (studyStartedToast). 실패면 null.
 */
export async function subscribeAndRefetch(
  setDynState: SetPlanDynamicState,
  planId: StudyPlanId,
  expectedExamSessionId: string,
  setLoadError: (message: string | null) => void,
  todayISO: string,
  mode: "start" | "restore" = "start",
): Promise<SubscribeResult | null> {
  let res;
  try {
    res = mode === "restore"
      ? await subscribeStudyPlan(planId, expectedExamSessionId, "restore")
      : await subscribeStudyPlan(planId, expectedExamSessionId);
  } catch (error) {
    if (!recoverStudyPlanRequest(error, setLoadError)) {
      const message =
        mode === "restore"
          ? "계획 되돌리기 결과를 확인하지 못했어요. 페이지를 새로고침해주세요."
          : "학습 시작 결과를 확인하지 못했어요. 페이지를 새로고침해주세요.";
      setLoadError(message);
      toast.error(message);
    } else if (mode === "restore") {
      toast.error("계획 되돌리기 결과를 확인하지 못했어요. 페이지를 새로고침해주세요.");
    }
    return null;
  }
  if (!res.success) {
    toast.error(mode === "restore" ? "계획을 되돌리지 못했어요." : (res.error ?? "학습 계획 시작에 실패했어요."));
    return null;
  }
  try {
    // 쓰기가 끝날 때 활성 회차가 바뀌더라도, 작업을 시작한 회차의 상태만
    // 현재 화면에 반영한다. /my/schedule은 쓰기 중 회차 선택도 잠근다.
    const next = await getAllPlansDynamicState(expectedExamSessionId);
    setDynState(next.plans);
    setLoadError(null);
    // 시작일은 방금 저장돼 돌아온 값(= plan.startDateISO)으로 판정한다.
    const startDateISO = next.plans[planId]?.startDateISO ?? todayISO;
    const syncPlanIds = mode === "restore" ? [] : subscriptionSyncPlanIds(planId, next.plans, expectedExamSessionId);
    return {
      ...(syncPlanIds.length ? { syncPlanIds } : {}),
      planId,
      startDateISO,
      timing: startTimingOf(startDateISO, todayISO),
    };
  } catch (error) {
    if (recoverStudyPlanRequest(error, setLoadError)) {
      if (mode === "restore") toast.error("계획은 되돌렸지만 일정을 불러오지 못했어요. 페이지를 새로고침해주세요.");
      return null;
    }
    // 사본 없이 최신 계획을 낙관적으로 그리면 방금 보존한 날짜와 다른 일정이 나타난다.
    const message =
      mode === "restore"
        ? "계획은 되돌렸지만 일정을 불러오지 못했어요. 페이지를 새로고침해주세요."
        : "학습은 시작됐지만 일정을 불러오지 못했어요. 페이지를 새로고침해주세요.";
    setLoadError(message);
    toast.error(message);
    return null;
  }
}
